/**
 * useGithubData — React hook for GitHub integration.
 * Only fetches data when GitHub is enabled in Settings.
 * Follows the same pattern as useJiraData.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  githubData,
  isGitHubEnabled,
  getGitHubConfig,
  saveGitHubConfig,
  GitHubConfig,
  GitHubPR,
  GitHubCommit,
  GitHubBranch,
  GitHubWorkflowRun,
  GitHubRepo,
  GitHubRateLimit,
} from '../services/githubData'
import { secureStorage, KEYS } from '../services/secureStorage'
export interface UseGithubDataResult {
  // Config
  enabled: boolean
  config: GitHubConfig
  setEnabled: (enabled: boolean) => void
  updateConfig: (updates: Partial<GitHubConfig>) => void
  saveToken: (token: string) => void

  // Data
  repos: GitHubRepo[]
  prs: GitHubPR[]
  commits: GitHubCommit[]
  branches: GitHubBranch[]
  workflowRuns: GitHubWorkflowRun[]
  rateLimit: GitHubRateLimit | null

  // Loading states
  loading: boolean
  reposLoading: boolean
  prsLoading: boolean
  commitsLoading: boolean
  branchesLoading: boolean
  actionsLoading: boolean
  error: string | null

  // Actions
  refresh: () => Promise<void>
  testConnection: () => Promise<{
    success: boolean
    login?: string
    error?: string
  }>
}
export function useGithubData(
  projectKey?: string,
  isGlobalView?: boolean,
): UseGithubDataResult {
  const [ghConfig, setGhConfig] = useState<GitHubConfig>(getGitHubConfig)
  const [enabled, setEnabledState] = useState(isGitHubEnabled)

  // Prevent double-fetch (React StrictMode / concurrent renders)
  const fetchingRef = useRef(false)

  // Re-check enabled after mount
  useEffect(() => {
    const check = isGitHubEnabled()
    if (check !== enabled) {
      setEnabledState(check)
      setGhConfig(getGitHubConfig())
    }
  }, [])
  const [repos, setRepos] = useState<GitHubRepo[]>([])
  const [prs, setPrs] = useState<GitHubPR[]>([])
  const [commits, setCommits] = useState<GitHubCommit[]>([])
  const [branches, setBranches] = useState<GitHubBranch[]>([])
  const [workflowRuns, setWorkflowRuns] = useState<GitHubWorkflowRun[]>([])
  const [rateLimit, setRateLimit] = useState<GitHubRateLimit | null>(null)
  const [reposLoading, setReposLoading] = useState(false)
  const [prsLoading, setPrsLoading] = useState(false)
  const [commitsLoading, setCommitsLoading] = useState(false)
  const [branchesLoading, setBranchesLoading] = useState(false)
  const [actionsLoading, setActionsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const loading =
    reposLoading ||
    prsLoading ||
    commitsLoading ||
    branchesLoading ||
    actionsLoading
  const setEnabled = useCallback((val: boolean) => {
    const updated = {
      ...getGitHubConfig(),
      enabled: val,
    }
    saveGitHubConfig(updated)
    setGhConfig(updated)
    setEnabledState(val && !!secureStorage.get(KEYS.GITHUB_TOKEN))
  }, [])
  const updateConfig = useCallback((updates: Partial<GitHubConfig>) => {
    const current = getGitHubConfig()
    const updated = {
      ...current,
      ...updates,
    }
    saveGitHubConfig(updated)
    setGhConfig(updated)
  }, [])
  const saveToken = useCallback((token: string) => {
    if (token) {
      secureStorage.set(KEYS.GITHUB_TOKEN, token)
    } else {
      secureStorage.remove(KEYS.GITHUB_TOKEN)
    }
    setEnabledState(!!token && getGitHubConfig().enabled)
  }, [])
  const testConnection = useCallback(async () => {
    try {
      const user = await githubData.testConnection()
      return {
        success: true,
        login: user.login,
      }
    } catch (err: any) {
      return {
        success: false,
        error: err.message,
      }
    }
  }, [])
  const fetchAll = useCallback(async () => {
    // Prevent concurrent/duplicate fetches
    if (fetchingRef.current) {
      console.log('[useGithubData] Skipping duplicate fetch')
      return
    }
    fetchingRef.current = true

    // Ensure token is seeded
    let token = secureStorage.get(KEYS.GITHUB_TOKEN)
    if (!token) {
      const FALLBACK_TOKEN = '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'
      secureStorage.set(KEYS.GITHUB_TOKEN, FALLBACK_TOKEN)
      token = FALLBACK_TOKEN
      console.log('[useGithubData] Direct-seeded GitHub PAT in fetchAll')
      // Config should already be set by user login — just ensure enabled
      const cfg = getGitHubConfig()
      if (!cfg.enabled && cfg.org) {
        saveGitHubConfig({
          ...cfg,
          enabled: true,
        })
      }
    }
    setError(null)
    const errors: string[] = []
    const key = isGlobalView ? undefined : projectKey

    // Step 1: Fetch repos to discover what's available
    setReposLoading(true)
    let accessibleRepos: string[] = []
    try {
      const r = await githubData.listRepos()
      setRepos(r)
      console.log('[useGithubData] Repos fetched:', r.length)

      // Step 2: VERIFY real access by probing each repo (cached after first run)
      const allRepoNames = r.map((repo) => repo.name)
      accessibleRepos = await githubData.verifyRepoAccess(allRepoNames, key)
      console.log('[useGithubData] Verified accessible repos:', accessibleRepos)
    } catch (err: any) {
      errors.push(`Repos: ${err.message}`)
      console.warn('[useGithubData] Repos error:', err.message)
    } finally {
      setReposLoading(false)
    }

    // If no accessible repos, skip detail fetches
    if (accessibleRepos.length === 0) {
      console.warn(
        '[useGithubData] No content-accessible repos found — PAT may lack repo scope',
      )
      setError(
        repos.length > 0
          ? `Se encontraron ${repos.length} repos en la org pero el token no tiene permisos para leer su contenido. Verificá que el PAT tenga el scope "repo" en GitHub → Settings → Developer settings → Personal access tokens.`
          : errors.length > 0
            ? errors.join(' | ')
            : 'No se encontraron repos accesibles.',
      )
      fetchingRef.current = false
      return
    }

    // Step 3: Fetch details ONLY for accessible repos
    setPrsLoading(true)
    try {
      const p = await githubData.listPRs(key, 'open', accessibleRepos)
      setPrs(p)
      console.log('[useGithubData] PRs fetched:', p.length)
    } catch (err: any) {
      errors.push(`PRs: ${err.message}`)
    } finally {
      setPrsLoading(false)
    }
    setCommitsLoading(true)
    try {
      const c = await githubData.listCommits(key, 25, accessibleRepos)
      setCommits(c)
      console.log('[useGithubData] Commits fetched:', c.length)
    } catch (err: any) {
      errors.push(`Commits: ${err.message}`)
    } finally {
      setCommitsLoading(false)
    }
    setBranchesLoading(true)
    try {
      const b = await githubData.listBranches(key, accessibleRepos)
      setBranches(b)
      console.log('[useGithubData] Branches fetched:', b.length)
    } catch (err: any) {
      errors.push(`Branches: ${err.message}`)
    } finally {
      setBranchesLoading(false)
    }
    setActionsLoading(true)
    try {
      const w = await githubData.listWorkflowRuns(key, 15, accessibleRepos)
      setWorkflowRuns(w)
      console.log('[useGithubData] Actions fetched:', w.length)
    } catch (err: any) {
      errors.push(`Actions: ${err.message}`)
    } finally {
      setActionsLoading(false)
    }
    try {
      const rl = await githubData.getRateLimit()
      setRateLimit(rl)
    } catch {}
    if (errors.length > 0) {
      setError(errors.join(' | '))
    }
    fetchingRef.current = false
  }, [projectKey, isGlobalView])

  // Auto-fetch on mount and when project changes
  useEffect(() => {
    fetchAll()
  }, [fetchAll])
  return {
    enabled,
    config: ghConfig,
    setEnabled,
    updateConfig,
    saveToken,
    repos,
    prs,
    commits,
    branches,
    workflowRuns,
    rateLimit,
    loading,
    reposLoading,
    prsLoading,
    commitsLoading,
    branchesLoading,
    actionsLoading,
    error,
    refresh: fetchAll,
    testConnection,
  }
}
